import { StoreProduct, StoreProductVariant } from "@/types/store"
import { Dialog, Transition } from "@headlessui/react"
import { Fragment, useEffect, useState, useTransition } from "react"
import Image from "next/image"
import { Heart, Loader2, ShoppingCart } from "lucide-react"
import { useRouter } from "next/navigation"
import toast from "react-hot-toast"

import { addFavorite, removeFavorite } from "@/lib/data/favorites"
import { addToCart } from "@lib/data/cart"
import {
  canQuickAddVariant,
  getProductVariantParam,
} from "@/lib/util/product-variant-url"
import { toast as shadcnToast } from "@/hooks/use-toast"
import { resolveImageUrl } from "@/lib/util/image-url"
import { useTranslation } from "react-i18next"

const NEXT_PUBLIC_S3_IMAGE_BASE_URL =
  process.env.NEXT_PUBLIC_S3_IMAGE_BASE_URL

type VariantSelectionModalMode = "favorite" | "addToCart"

type VariantSelectionModalProps = {
  product: StoreProduct
  isOpen: boolean
  onClose: () => void
  mode?: VariantSelectionModalMode
}

type FavoriteState = {
  is_favorite: boolean
  favoriteId?: string | number | null
}

export default function VariantSelectionModal({
  product,
  isOpen,
  onClose,
  mode = "favorite",
}: VariantSelectionModalProps) {
  const [, startTransition] = useTransition()
  const router = useRouter()
  const { t } = useTranslation()

  const [favoriteMap, setFavoriteMap] = useState<
    Record<string | number, FavoriteState>
  >({})

  useEffect(() => {
    if (!product?.variants) return

    const initialState: Record<string | number, FavoriteState> = {}

    product.variants.forEach((variant) => {
      initialState[variant.id] = {
        is_favorite: variant.is_favorite || false,
        favoriteId: variant.favorite_id,
      }
    })

    setFavoriteMap(initialState)
  }, [product])

  const [loadingStates, setLoadingStates] = useState<
    Record<string | number, boolean>
  >({})
  const [quantities, setQuantities] = useState<
    Record<string | number, number>
  >({})

  const handleToggleFavorite = (variant: StoreProductVariant) => {
    if (loadingStates[variant.id]) return
    setLoadingStates((prev) => ({ ...prev, [variant.id]: true }))
    startTransition(async () => {
      try {
        const current = favoriteMap[variant.id]

        if (current?.is_favorite) {
          const result = await removeFavorite(String(variant.id))

          if (!result.success) {
            shadcnToast({
              title: t("variantSelectionModal.favoriteError"),
              description: result.message,
              variant: "destructive",
            })
            return
          }
          setFavoriteMap((prev) => ({
            ...prev,
            [variant.id]: {
              is_favorite: false,
              favoriteId: null,
            },
          }))

          shadcnToast({
            title: t("variantSelectionModal.favoriteRemoved"),
          })
          return
        }

        const result = await addFavorite(variant.id)

        if (!result.success) {
          shadcnToast({
            title: result.message,
            variant: "destructive",
          })
          return
        }

        setFavoriteMap((prev) => ({
          ...prev,
          [variant.id]: {
            is_favorite: true,
            favoriteId: result.favoriteId,
          },
        }))

        shadcnToast({
          title: t("variantSelectionModal.favoriteAdded"),
        })
      } catch (error: any) {
        shadcnToast({
          title: error?.message || t("variantSelectionModal.unexpectedError"),
          variant: "destructive",
        })
      } finally {
        setLoadingStates((prev) => ({ ...prev, [variant.id]: false }))
      }
    })
  }

  const handleAddVariantToCart = async (variant: StoreProductVariant) => {
    if (loadingStates[variant.id]) return

    if (!canQuickAddVariant(variant)) {
      const variantParam = getProductVariantParam(variant.title)
      router.push(
        `/products/${product.handle}${variantParam ? `?variant=${variantParam}` : ""}`
      )
      onClose()
      return
    }

    setLoadingStates((prev) => ({ ...prev, [variant.id]: true }))
    try {
      await addToCart({
        variantId: String(variant.id),
        quantity: quantities[variant.id] || 1,
        formAnswers: {},
      })
      toast.success(t("product.addToCart"))
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("cart:open"))
      }
    } catch (error: any) {
      toast.error(error?.message || t("product.addToCartFailed"))
    } finally {
      setLoadingStates((prev) => ({ ...prev, [variant.id]: false }))
    }
  }

  const modalTitle =
    mode === "addToCart"
      ? t("variantSelectionModal.addToCartTitle", {
          product: product.title,
        })
      : t("variantSelectionModal.title", {
          product: product.title,
        })

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative" style={{ zIndex: 24 }} onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left shadow-xl transition-all">
                <Dialog.Title className="text-lg font-medium text-gray-900">
                  {modalTitle}
                </Dialog.Title>

                <div className="mt-4">
                  {product.variants?.length ? (
                    <ul className="divide-y divide-gray-200">
                      {product.variants.map((variant) => {
                        const favorite = favoriteMap[variant.id]
                        const isFavorited = favorite?.is_favorite || false
                        const canAdd = canQuickAddVariant(variant)

                        const variantThumbnail =
                          variant.images?.find(
                            (img) => img.is_thumbnail === 1
                          ) || variant.images?.[0]

                        const productThumbnail =
                          product.images?.find(
                            (img) => img.is_thumbnail === 1
                          ) || product.images?.[0]

                        const thumbnail = variantThumbnail || productThumbnail

                        return (
                          <li
                            key={variant.id}
                            className="flex items-center justify-between py-3"
                          >
                            <div className="flex items-center gap-3">
                              {thumbnail && (resolveImageUrl(thumbnail.image_key) ?? NEXT_PUBLIC_S3_IMAGE_BASE_URL) ? (
                                <Image
                                  src={resolveImageUrl(thumbnail.image_key) || ""}
                                  alt={variant.title || "Variant image"}
                                  width={40}
                                  height={40}
                                  className="rounded object-cover"
                                />
                              ) : (
                                <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-200 text-xs text-gray-500">
                                  {t("variantSelectionModal.noImage")}
                                </div>
                              )}

                              <span className="text-sm text-gray-900">
                                {variant.title}
                              </span>
                            </div>

                            {mode === "addToCart" ? (
                              <div className="flex items-center gap-2">
                                <div className="flex items-center rounded border border-gray-300">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setQuantities((prev) => ({
                                        ...prev,
                                        [variant.id]: Math.max(
                                          1,
                                          (prev[variant.id] || 1) - 1
                                        ),
                                      }))
                                    }
                                    className="px-2 py-1 text-sm hover:bg-gray-100"
                                  >
                                    -
                                  </button>
                                  <span className="w-8 text-center text-sm">
                                    {quantities[variant.id] || 1}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setQuantities((prev) => ({
                                        ...prev,
                                        [variant.id]: (prev[variant.id] || 1) + 1,
                                      }))
                                    }
                                    className="px-2 py-1 text-sm hover:bg-gray-100"
                                  >
                                    +
                                  </button>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleAddVariantToCart(variant)}
                                  aria-label={
                                    canAdd
                                      ? t("product.addToCart")
                                      : t("product.viewDetails")
                                  }
                                  className={`inline-flex h-8 w-8 items-center justify-center rounded-full border border-orange-200 bg-white text-orange-600 transition-colors hover:bg-orange-50 ${
                                    loadingStates[variant.id]
                                      ? "cursor-not-allowed opacity-50"
                                      : ""
                                  }`}
                                >
                                  {loadingStates[variant.id] ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <ShoppingCart className="h-4 w-4" />
                                  )}
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleToggleFavorite(variant)}
                                aria-label={
                                  isFavorited
                                    ? t("variantSelectionModal.removeFavorite")
                                    : t("variantSelectionModal.addFavorite")
                                }
                                className={`inline-flex h-8 w-8 items-center justify-center rounded-full border border-orange-200 bg-white text-orange-600 transition-colors hover:bg-orange-50 ${
                                  loadingStates[variant.id]
                                    ? "cursor-not-allowed opacity-50"
                                    : ""
                                }`}
                              >
                                {loadingStates[variant.id] ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Heart
                                    className="h-4 w-4"
                                    fill={
                                      isFavorited ? "currentColor" : "none"
                                    }
                                  />
                                )}
                              </button>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">
                      {t("variantSelectionModal.noVariants")}
                    </p>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
